import * as THREE from 'three';
import { addArmorLook, strips, type ArmorKit } from './human-armors.js';

/**
 * Costume clothing in the manner of 90s fantasy paperbacks and posters: satin gowns slit to the hip, necklines plunging to the waist,
 * gold at every cuff and collar. They are everyday garments (`HUMAN_CLOTHING`), so any civilian or quest character can wear one, and they
 * are dressed on the bare body like the other clothing. What is new is two cut surfaces, a skirt with wedges taken out of it and a bodice
 * with a V taken out of the front, so the leg and the chest really show through an opening instead of a texture.
 *
 * Layers lie on the body at least 8 mm apart: two cloth sheets closer than that fight over the same pixels (see `human-model.ts`).
 */
export const costumeLooks: Record<string, (k: ArmorKit) => void> = {};
export const parts = (k: ArmorKit) => ({ ...k.at, ...k.m });
const { clamp, lerp } = THREE.MathUtils;

interface SplitGown {
  /** Where the skirt ends and how far it flares there; `top` and `bottom` choose the band drawn (a hem trim is a narrow band). */
  hem: number; flare: number; grow: number; top: number; bottom: number;
  /** Slits open from this height down, centred on these angles (0 is the front, positive toward the viewer's right, pi the back) and `width` radians wide at the hem. */
  slitTop: number; slits: number[]; width: number;
  /** How wide a slit already is where it starts, as a fraction of its width: 0 is a seam closing at the hip, 1 a skirt open to the waist. */
  spread?: number; material: THREE.Material;
  /** An oblique hem: the hem rises by `amount` metres at the angle `phase` and falls back to the true hem on the opposite side, easing in over the skirt from `top`. */
  tilt?: { amount: number; phase: number; top: number };
  /** A ragged hem bitten into points up to this many metres deep. */
  ragged?: number;
  /** Trim sewn along both edges of every slit. */
  piping?: THREE.Material;
}
/** A skirt with slits cut up from the hem. Each stretch of cloth between slits is its own skinned sheet, so the leg shows through the gap. */
export function splitGown(k: ArmorKit, o: SplitGown) {
  const release = k.at.h + .06;
  if (o.top > release) k.gown(o.hem, o.flare, o.grow, o.material, Math.max(o.bottom, release), o.top);
  const top = Math.min(o.top, release);
  if (o.bottom >= top) return;
  // Long unbroken panels need the same angular detail as a closed gown. Otherwise
  // the leg shader clears the vertices but a broad triangle still cuts through a calf.
  const rows = Math.max(2, Math.ceil(Math.abs(top - o.bottom) / .025)), centres = [...o.slits].sort((a, b) => a - b);
  // Rows run from the top down so the sheets face outward.
  const ys = Array.from({ length: rows + 1 }, (_, i) => lerp(top, o.bottom, i / rows));
  const half = (y: number) => centres.length ? o.width / 2 * lerp(o.spread ?? 0, 1, clamp((o.slitTop - y) / (o.slitTop - o.hem), 0, 1) ** .85) * (y < o.slitTop ? 1 : 0) : 0;
  const arcs = centres.length ? centres.map((from, i) => ({ from, to: centres[i + 1] ?? centres[0] + Math.PI * 2 })) : [{ from: 0, to: Math.PI * 2 }];
  /** How far the hem is moved at a point of the skirt: lifted obliquely, or bitten down into teeth. */
  const shift = (y: number, theta: number) => {
    let dy = 0;
    if (o.tilt) {
      // An oblique hem must fade out before the fitted join, rather than moving that edge above the upper fabric.
      const start = Math.min(o.tilt.top, release);
      dy += o.tilt.amount * (.5 + .5 * Math.cos(theta - o.tilt.phase)) * clamp((start - y) / (start - o.hem), 0, 1) ** 1.5;
    }
    if (o.ragged) { const t = ((theta / (Math.PI * 2) * 11) % 1 + 1) % 1; dy -= o.ragged * clamp(1 - (y - o.hem) / .07, 0, 1) ** 1.2 * Math.abs(t * 2 - 1); }
    return dy;
  };
  for (const arc of arcs) {
    const cage = `split:${JSON.stringify([o.hem, o.flare, o.slitTop, o.slits, o.width, o.spread, o.tilt, o.ragged, arc])}`;
    const columns = Math.max(8, Math.ceil((arc.to - arc.from) / (Math.PI * 2) * 96));
    if (o.piping && centres.length) {
      // A narrow band along each cut edge, standing proud of the cloth (and of any hem band) by more than a centimetre.
      const cut = ys.filter(y => y < o.slitTop);
      if (cut.length > 3) for (const start of [true, false]) {
        k.hang(cut.map(y => { const edge = start ? arc.from + half(y) : arc.to - half(y), [from, to] = start ? [edge, edge + .07] : [edge - .07, edge];
          return [0, 1, 2].map(j => { const theta = lerp(from, to, j / 2); return k.gownSpot(o.hem, o.flare, o.grow + .024, y + shift(y, theta), theta).point; }); }), o.piping, o.grow + .024, undefined, cage);
      }
    }
    k.hang(ys.map(y => Array.from({ length: columns + 1 }, (_, j) => { const theta = lerp(arc.from + half(y), arc.to - half(y), j / columns); return k.gownSpot(o.hem, o.flare, o.grow, y + shift(y, theta), theta).point; })), o.material, o.grow, undefined, cage);
  }
}

interface SoftSkirt {
  top: number; hem: number; grow: number;
  /** How far the skirt stands off the body by its hem, and how many soft folds run down it, and how far the hem dips between its high points (metres). */
  flare: number; folds: number; drop: number; material: THREE.Material;
  /** The hem rises by `amount` metres at the angle `phase` (0 the front, positive toward the viewer's right): a wrap cut on a diagonal. */
  tilt?: { amount: number; phase: number };
}
/**
 * A short skirt that hangs the way cloth does. It follows the body's own surface down from the waist and stands off it a little more with
 * every row, with folds that deepen toward the hem and a hem that dips and rises round the hips instead of a level, stiff ring.
 */
export function softSkirt(k: ArmorKit, o: SoftSkirt) {
  const rows = 18, across = 20, ys = Array.from({ length: rows + 1 }, (_, i) => lerp(o.top, o.hem, i / rows));
  k.hang(ys.map((y, r) => {
    const t = r / rows, ring: THREE.Vector3[] = [];
    const at = (s: number) => {
      const theta = s * Math.PI * 2, front = s < .5, along = front ? s * 2 : (s - .5) * 2;
      const grow = o.grow + o.flare * t ** 1.2 + .016 * t ** 1.5 * Math.sin(o.folds * theta + .6) + .008 * t ** 1.5 * Math.sin(o.folds * 2.3 * theta + 2);
      const lift = o.tilt ? o.tilt.amount * (.5 + .5 * Math.cos(theta - o.tilt.phase)) * t ** 1.5 : 0;
      const down = y - o.drop * t ** 3 * (.5 + .5 * Math.sin(3 * theta + 1.1)) + lift, ringY = Math.max(down, k.at.h + .02), reach = (k.pelvisAt(ringY).rx + grow) * .985;
      // The body narrows below the hips but cloth does not: it keeps the width it left them with and only its height follows the hem.
      const point = k.onTorso(lerp(front ? -reach : reach, front ? reach : -reach, along), ringY, grow, front ? 1 : -1).point; point.y = down;
      return point;
    };
    for (let i = 0; i < across * 2; i++) ring.push(at(i / (across * 2)));
    ring.push(ring[0].clone());
    return ring;
  }), o.material, o.grow + .02, undefined, `soft:${JSON.stringify([o.hem, o.flare, o.folds, o.drop, o.tilt])}`);
}

interface VBodice { top: number; bottom: number; grow: number; inner: number; plunge: number; material: THREE.Material; /** Leaves the back bare: the cloth ends at the sides. */ openBack?: boolean;
  /** A V cut in the back too: half the opening at `top`, closing at `backPlunge` (the front's `plunge` when not given). */ backInner?: number; backPlunge?: number;
  /** Strapless: the edge runs on across the shoulder ports (see `torsoLayer`). */ shoulders?: boolean;
  /** The edge curves down from beside the V to under the arm, like a sleeveless dress, so a top at the shoulder clears the arm (see `armhole`). */ armhole?: boolean }
/**
 * An armhole cut for a top whose edge sits at the shoulder: level out to `from` (beside a V, where a halter strap starts), then a
 * quarter circle down to the side under the arm. It ends where the cloth carried over the shoulder bridge ends (`coverAt`) and as
 * wide as it can while staying inside that edge, so the bridge's edge carries it on without a step.
 */
export function armhole(k: ArmorKit, top: number, from: number) {
  const ends = [false, true].map(back => {
    const ring: [number, number][] = [];
    for (let drop = 0; drop < .15; drop += .005) { const reach = k.coverAt(top - drop, back); if (reach === Infinity) break; ring.push([drop, reach]); }
    if (!ring.length) return { to: from + .02, depth: .06 };
    const depth = ring[ring.length - 1][0] + .01;
    const inside = (to: number) => ring.every(([drop, reach]) => from + (to - from) * Math.sqrt(1 - (1 - drop / depth) ** 2) <= reach + .005);
    let to = Math.max(ring[ring.length - 1][1], from + .02);
    while (to > from + .02 && !inside(to)) to -= .002;
    return { to, depth };
  });
  return (point: THREE.Vector3) => {
    const { to, depth } = ends[Number(point.z < k.at.socketZ)], t = clamp((Math.abs(point.x) - from) / (to - from), 0, 1);
    return top - depth * (1 - Math.sqrt(1 - t * t)) - point.y;
  };
}

/**
 * A bodice that goes round the torso but is open down the front in a V: `inner` is half the opening at `top`, closing to nothing at
 * `plunge`. Its cuts preserve the connected skin's anatomy and bone field; this fitted cloth has no skirt collision.
 */
export function vBodice(k: ArmorKit, o: VBodice) {
  const middle = (point: THREE.Vector3) => point.z - k.pelvisAt(point.y).z;
  const cuts: ((point: THREE.Vector3) => number)[] = [];
  if (o.openBack) cuts.push(middle);
  if (o.inner) cuts.push(point => Math.max(-middle(point), Math.abs(point.x) - o.inner * clamp((point.y - o.plunge) / (o.top - o.plunge), 0, 1)));
  if (o.backInner) {
    const plunge = o.backPlunge ?? o.plunge;
    cuts.push(point => Math.max(middle(point), Math.abs(point.x) - o.backInner! * clamp((point.y - plunge) / (o.top - plunge), 0, 1)));
  }
  if (o.armhole) cuts.push(armhole(k, o.top, Math.max(o.inner, .06) + .015));
  k.bodyPanel(o.bottom, o.top, o.grow, o.material, cuts, o.shoulders);
}

interface HighCut { top: number; cut: number; bottom: number; grow: number; narrow: number; material: THREE.Material }
/**
 * A high-cut leotard follows the branching pelvis, narrowing below `cut` into its connected crotch gusset.
 */
export function highCut(k: ArmorKit, o: HighCut) {
  const width = (y: number) => lerp(o.narrow, k.pelvisAt(y).rx, clamp((y - o.bottom) / (o.cut - o.bottom), 0, 1) ** 2.2);
  // The gusset is the connected crotch itself, with the same hip/thigh influences as the skin.
  k.bodyPanel(Math.min(o.bottom, k.at.crotch - .035), o.top, o.grow, o.material,
    [point => Math.max(point.y - o.cut, width(point.y) - Math.abs(point.x))]);
}

interface GroinCloth {
  /** Where it hangs from (the belt), how far it falls, how wide it is at the top and how much it narrows toward the hem (0 square, 1 a point). */
  top: number; length: number; width: number; point: number;
  /** How far the hem dips at the middle and how far it stands off the thighs at the hem. */
  dip: number; out: number; grow: number;
  material: THREE.Material;
  /** A band of trim along the hem, a row of tassels and a hanging stone. A groin cloth hangs front and back unless `front` or `back` is false. */
  trim?: THREE.Material; fringe?: THREE.Material; tassels?: number; stone?: THREE.Material; back?: boolean; front?: boolean;
  /** A raw hem, torn into uneven teeth up to this many metres deep. */
  raw?: number;
}
/**
 * A groin cloth: a panel of cloth hanging from the belt over the front (and optionally the back) of the pelvis, narrowing toward a hem that
 * dips at the middle, trimmed with a band, tassels and a hanging stone. It is cloth like a skirt, so the legs push it aside as they swing, and
 * everything on it is sewn into the same cloth rather than fixed to the body.
 */
export function groinCloth(k: ArmorKit, o: GroinCloth) {
  const rows = 12, columns = o.raw ? 20 : 8, hipY = k.at.h + .02;
  const widthAt = (t: number) => lerp(o.width, o.width * (1 - o.point), clamp(t, 0, 1) ** 1.4);
  /** A point of the cloth at `u` across (-1 to 1) and `t` of the way down (past 1 hangs below the hem). */
  const spot = (u: number, t: number, grow: number, face: number) => {
    const y = o.top - o.length * t, point = k.onTorso(u * widthAt(t) * face, Math.max(y, hipY), grow + o.out * clamp(t, 0, 1), face).point;
    point.y = y - o.dip * (1 - u * u) * clamp(t, 0, 1) ** 2;
    if (o.raw) { // torn, not cut: teeth of different depths along the hem, deepest at the last row
      const along = ((u + 1) / 2 * 9) % 1, tooth = Math.abs(along * 2 - 1) * (.65 + .35 * Math.sin(u * 7.3 + 1));
      point.y -= o.raw * tooth * clamp((t - .78) / .22, 0, 1) ** 1.5;
    }
    return point;
  };
  const gap = o.grow + .02;
  for (const face of [...o.front === false ? [] : [1], ...o.back === false ? [] : [-1]]) {
    // Trim, tassels and stone are sewn into the panel, so they share its cloth cage.
    const cage = `groin:${JSON.stringify([o.top, o.width, o.length, o.dip, o.out, face])}`;
    const band = (from: number, to: number, grow: number, material: THREE.Material) =>
      k.hang(Array.from({ length: rows + 1 }, (_, r) => Array.from({ length: columns + 1 }, (_, c) => spot(lerp(-1, 1, c / columns), lerp(from, to, r / rows), grow, face))), material, gap, undefined, cage, true);
    band(0, 1, o.grow, o.material);
    if (o.trim) band(.84, 1, o.grow + .01, o.trim);
    if (o.fringe && o.tassels) for (let i = 0; i < o.tassels; i++) {
      const u = lerp(-.88, .88, i / Math.max(1, o.tassels - 1)), head = spot(u, 1, o.grow + .012, face);
      // Each tassel is a narrow flat strip hanging from the hem.
      k.hang([0, 1, 2].map(r => [-1, 1].map(side => head.clone().add(new THREE.Vector3(side * face * .0065 * (1 - r * .3), -r * .028, r * .004)))), o.fringe, gap, undefined, cage, true);
    }
    if (o.stone) {
      const base = spot(0, 1, o.grow + .012, face);
      k.hang([0, 1, 2, 3].map(r => [-1, 0, 1].map(side => base.clone().add(new THREE.Vector3(side * face * .017 * (1 - r / 3) ** .8, -.004 - r * .022, r * .003)))), o.stone, gap, undefined, cage, true);
    }
  }
}

/**
 * A belt of cloth: the same stuff as a groin cloth hanging from it, wound three times round the hips in overlapping bands (each stands out from the
 * last by more than a centimetre, so none fights another), knotted at one side with two tails hanging from the knot.
 */
export function clothBelt(k: ArmorKit, o: { y: number; material: THREE.Material; side?: number }) {
  const { y, material } = o, side = o.side ?? 1;
  k.torsoLayer(y - .04, y + .01, .018, material); k.torsoLayer(y - .015, y + .035, .028, material); k.torsoLayer(y + .005, y + .05, .038, material);
  const reach = (k.pelvisAt(y).rx + .04) * .985, knot = k.onTorso(side * reach * .9, y, .04, 1).point, [parent, origin] = k.anchor(y);
  k.ellipsoid(parent, material, knot.x + side * .008, knot.y - origin, knot.z, .026, .024, .026);
  for (const [lean, length] of [[.25, .3], [-.2, .24]] as const) {
    k.hang([0, 1, 2, 3, 4].map(r => { const s = r / 4, at = knot.clone().add(new THREE.Vector3(side * (.016 + .035 * s * (1 + lean)), -.02 - length * s, .012 * s)); return [-1, 1].map(c => at.clone().add(new THREE.Vector3(0, -Math.abs(c) * .006 * s, c * (.016 - .006 * s)))); }), material, .06, undefined, `belt:${JSON.stringify([y, side, lean])}`, true);
  }
}

/** A chain round the neck with a stone hanging over the breastbone. */
export function pendant(k: ArmorKit, chainMaterial: THREE.Material, stone: THREE.Material, from: number) {
  const chain = Array.from({ length: 13 }, (_, i) => { const t = i / 12; return k.onTorso(lerp(-.075, .075, t), from - .11 * Math.sin(Math.PI * t), .034); });
  k.ribbon(chain, .008, .005, chainMaterial); k.plate(chainMaterial, .02, .008, chain[6]); k.stud(stone, chain[6], .014, .014);
}

/** A crescent moon, horns up, as a flat silver plate laid on the chest at (x, y) and `size` wide. */
export function crescent(k: ArmorKit, material: THREE.Material, x: number, y: number, size: number, grow = .04) {
  const outer = size / 2, inner = outer * .84, shift = outer * .42, meet = (outer * outer - inner * inner + shift * shift) / (2 * shift), across = Math.sqrt(outer * outer - meet * meet);
  const shape = new THREE.Shape(), right = Math.atan2(meet, across), steps = 24;
  // Out along the big circle from the right horn round the bottom to the left horn, then back along the small circle that bites into it.
  for (let i = 0; i <= steps; i++) { const angle = lerp(right, -Math.PI - right, i / steps), px = Math.cos(angle) * outer, py = Math.sin(angle) * outer; if (i) shape.lineTo(px, py); else shape.moveTo(px, py); }
  const from = Math.atan2(meet - shift, -across), to = Math.atan2(meet - shift, across);
  for (let i = 0; i <= steps; i++) { const angle = lerp(from + (from > 0 ? -Math.PI * 2 : 0), to, i / steps); shape.lineTo(Math.cos(angle) * inner, shift + Math.sin(angle) * inner); }
  const spot = k.onTorso(x, y, grow), [parent, origin] = k.anchor(y);
  k.mesh(parent, new THREE.ExtrudeGeometry(shape, { depth: .006, bevelEnabled: false }), material, spot.point.x, spot.point.y - origin, spot.point.z);
}

/** Lacing across the open V: short cross ribbons from the plunge up to the top. */
export function lacing(k: ArmorKit, o: { top: number; plunge: number; inner: number; count: number; material: THREE.Material }) {
  for (let i = 0; i < o.count; i++) {
    const y = lerp(o.plunge + .02, o.top - .03, i / Math.max(1, o.count - 1)), half = o.inner * clamp((y - o.plunge) / (o.top - o.plunge), 0, 1) + .006;
    k.ribbon(k.run([-half, y + .012], [half, y - .012], .012, 1, 0, 4), .009, .004, o.material);
  }
}

let netTexture: THREE.DataTexture | undefined;
/** A diamond lattice as an alpha map: the holes of fishnet. */
function net() {
  if (netTexture) return netTexture;
  const size = 32, data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const a = ((x + y) / size) % 1, b = ((x - y) / size % 1 + 1) % 1, line = Math.min(a, 1 - a) < .1 || Math.min(b, 1 - b) < .1, at = (y * size + x) * 4;
    data[at] = data[at + 1] = data[at + 2] = 255; data[at + 3] = line ? 255 : 0;
  }
  netTexture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  netTexture.wrapS = netTexture.wrapT = THREE.RepeatWrapping; netTexture.magFilter = THREE.LinearFilter; netTexture.minFilter = THREE.LinearMipmapLinearFilter; netTexture.generateMipmaps = true; netTexture.needsUpdate = true;
  return netTexture;
}
/** Fishnet in a colour: a lattice cut out of the cloth, `around` diamonds round a limb and `along` down it. */
export function fishnet(color: number, around = 7, along = 12) {
  const texture = net().clone(); texture.repeat.set(around, along); texture.needsUpdate = true;
  const material = new THREE.MeshStandardMaterial({ color, map: texture, alphaTest: .5, roughness: .6, side: THREE.DoubleSide }); material.name = 'cloth_dark';
  return material;
}
/** Long sleeves of sheer stocking from the wrist to the shoulder, ending in a band of trim. */
export function armStockings(k: ArmorKit, stocking: THREE.Material, trim: THREE.Material) {
  const { wrist, armTop } = k.at;
  k.armLayer(wrist + .003, armTop - .02, .008, stocking); k.armLayer(armTop - .035, armTop - .02, .016, trim);
}

/** Satin and sheen: costume cloth is shinier than a villager's. */
export const satin = (k: ArmorKit, color?: number) => { const material = k.mat('cloth', .12, .42, color); material.side = THREE.DoubleSide; return material; };

/** A flared collar standing behind the neck, lined with trim: the high back collar of every pulp sorceress. */
export function flaredCollar(k: ArmorKit, cloth: THREE.Material, trim: THREE.Material, height = .27) {
  const { neckBase, torsoY } = k.at, back = k.onTorso(0, neckBase, .012, -1), flare = new THREE.Shape();
  flare.moveTo(-.07, 0); flare.lineTo(.07, 0); flare.lineTo(.17, height * .8); flare.lineTo(.09, height * .62); flare.lineTo(0, height); flare.lineTo(-.09, height * .62); flare.lineTo(-.17, height * .8); flare.closePath();
  for (const [material, scale, push] of [[cloth, 1, 0], [trim, 1.06, .01]] as const) {
    const piece = k.mesh(k.torso, new THREE.ExtrudeGeometry(flare, { depth: .008, bevelEnabled: false }), material, 0, neckBase - torsoY - .01, back.point.z - .018 + push); piece.rotation.x = -.38; piece.scale.set(scale, scale, 1);
  }
}

/** Detached long gloves to above the elbow, with a cuff of trim. */
export function operaGloves(k: ArmorKit, cloth: THREE.Material, trim: THREE.Material) {
  const { wrist, elbow } = k.at;
  k.armLayer(wrist + .003, elbow + .14, .009, cloth); k.armLayer(elbow + .135, elbow + .15, .018, trim);
}

/** Stockings to the thigh in a sheer dark, each held by a garter of trim with a stone on the outside of the leg. */
export function garters(k: ArmorKit, stocking: THREE.Material, trim: THREE.Material, stone: THREE.Material, height: number) {
  const { ankle, h } = k.at;
  k.legLayer(ankle + .02, height, .008, stocking); k.legLayer(height - .016, height, .018, trim);
  for (const [limb, side] of [[k.leftLeg, -1], [k.rightLeg, 1]] as const) k.ellipsoid(limb.upper, stone, side * .075, -(h - height) - .01, .0, .014, .014, .014);
}

/** A pair of wings of gilded feathers swept up from each shoulder. */
export function wingPauldrons(k: ArmorKit, trim: THREE.Material) {
  const { shoulder, armTop } = k.at;
  for (const [limb, side] of [[k.leftArm, -1], [k.rightArm, 1]] as const) {
    k.armPiece(side, shoulder - .12, armTop, .028, trim);
    for (let i = 0; i < 4; i++) {
      const length = .2 - i * .03, lean = side * (.5 + i * .3), feather = k.mesh(limb.upper, new THREE.ConeGeometry(.016 - i * .002, length, 4), trim);
      feather.rotation.z = -lean; feather.scale.z = .3;
      feather.position.set(side * .03 + Math.sin(lean) * length / 2, armTop - shoulder + .02 + Math.cos(lean) * length / 2 - i * .004, -.015);
    }
  }
}

/** A chain slung low across the hips with a stone at the navel. */
export function bellyChain(k: ArmorKit, trim: THREE.Material, stone: THREE.Material) {
  const { h } = k.at, y = h + .045;
  const sag = Array.from({ length: 15 }, (_, i) => { const t = i / 14; return k.onTorso(lerp(-.1, .1, t), y - .035 * Math.sin(Math.PI * t) + .02, .05); });
  k.ribbon(sag, .008, .005, trim); k.stud(stone, k.onTorso(0, y - .02, .052), .014, .012);
}

/** What varies between the gowns: how long, where it is slit, how deep the neckline plunges, and what is added. */
interface Gown {
  /** Hem height above the ankle (a floor-length gown touches the ground), or `hemFromHip` above the hip joint instead (negative is down the thigh). */
  hem: number; hemFromHip?: number; flare: number;
  /** Slit centres in radians (0 the front, positive toward the viewer's right, pi the back), the height they start at above the ankle, and their width at the hem. */
  slits: number[]; slitTop: number; slitWidth: number; spread?: number;
  /** An oblique hem (see `SplitGown.tilt`), a ragged one, and whether the hem has a band of trim. */
  tilt?: { amount: number; phase: number }; ragged?: number; hemBand?: boolean;
  /** How far above the waist the V closes, and half the opening at the top. */
  plunge: number; inner: number;
  sleeves: 'bell' | 'gloves' | 'none';
  collar?: boolean; stockings?: boolean; wings?: boolean; chain?: boolean; strapless?: boolean; backless?: boolean;
  stocking?: number; stockingTop?: number;
  /** Stockings on the arms, sheer or fishnet, and fishnet on the legs. */
  armStockings?: 'sheer' | 'fishnet'; fishnet?: boolean;
  /** A wide embroidered hem with a ring of stones, and leather instead of gold at the waist. */
  ornate?: boolean; rugged?: boolean;
  /** Textile only: no gold at the hem, waist, neck or garters, and no chain. */
  plain?: boolean;
  /** A V cut in the back of the bodice (see `VBodice.backInner`). */
  backInner?: number; backPlunge?: number;
  /** Trim along the slits, and a pendant over the breastbone. */
  piping?: boolean; pendant?: boolean;
  /** Hangs the skirt as soft folds from the hip instead of the stiff bell (see `softSkirt`): how far it stands off the body, its folds and how far its hem dips. */
  soft?: { flare: number; folds: number; drop: number };
}
function gownLook(o: Gown) {
  return (k: ArmorKit) => {
    const { h, crotch, waist, s, shoulder, elbow, wrist, neckBase, ankle, gold, gem, clothDark, leather } = parts(k);
    const dress = satin(k), hem = o.hemFromHip !== undefined ? h + o.hemFromHip : ankle + o.hem, top = o.strapless ? s - .035 : s - .01, trim = o.plain ? clothDark : gold, girdle = o.rugged ? leather : o.plain ? clothDark : gold;
    k.torsoLayer(crotch + .001, h + .06, .008, o.hemFromHip !== undefined ? dress : clothDark); // so a slit shows a thigh and nothing else; a short dress gets one in its own colour
    const skirt = { hem, flare: o.flare, grow: .016, top: waist + .02, bottom: hem, slitTop: ankle + o.slitTop, slits: o.slits, width: o.slitWidth, spread: o.spread,
      tilt: o.tilt && { ...o.tilt, top: waist + .02 }, ragged: o.ragged };
    if (o.soft) softSkirt(k, { top: waist + .02, hem, grow: .02, ...o.soft, material: dress }); else splitGown(k, { ...skirt, material: dress, piping: o.piping && !o.plain ? trim : undefined });
    if (o.hemBand !== false && !o.plain) {
      const band = o.ornate ? .13 : .05;
      splitGown(k, { ...skirt, ragged: undefined, grow: .026, top: hem + band, material: trim });
      if (o.ornate) {
        splitGown(k, { ...skirt, ragged: undefined, grow: .026, top: hem + .17, bottom: hem + .15, material: trim });
        splitGown(k, { ...skirt, ragged: undefined, grow: .036, top: hem + .078, bottom: hem + .062, material: gem }); // a stripe of stones sewn into the hem
      }
    }
    vBodice(k, { top, bottom: waist - .04, grow: .013, inner: o.inner, plunge: waist + o.plunge, material: dress, openBack: o.backless, backInner: o.backInner, backPlunge: o.backPlunge === undefined ? undefined : waist + o.backPlunge,
      shoulders: true, armhole: !o.strapless });
    k.torsoLayer(waist - .04, waist + .03, .024, girdle); if (!o.plain) k.stud(gem, k.onTorso(0, waist - .005, .03), .016, .012);
    k.neckLoft(neckBase - .004, neckBase + .03, .014, trim);
    if (!o.strapless) for (const side of [-1, 1]) k.ribbon(k.run([side * o.inner, top], [side * .034, neckBase - .012], .02, 1, 0, 6), .02, .006, dress);
    if (o.sleeves === 'bell') for (const side of [-1, 1]) {
      k.armPiece(side, wrist, elbow + .12, .011, dress);
      for (let i = 0; i < 3; i++) k.armPiece(side, wrist - .004 + i * .035, wrist + .04 + i * .035, .042 - i * .011, dress);
      k.armPiece(side, wrist - .004, wrist + .008, .053, trim); k.armPiece(side, shoulder - .13, shoulder - .115, .03, trim);
    } else if (o.sleeves === 'gloves') operaGloves(k, dress, trim);
    if (o.collar) flaredCollar(k, dress, trim);
    const stockingTop = h + (o.stockingTop ?? -.06);
    if (o.stockings) garters(k, o.fishnet ? fishnet(o.stocking ?? 0x15141a, 7, 16) : o.stocking === undefined ? clothDark : k.mat('dark', 0, .35, o.stocking), trim, gem, stockingTop);
    if (o.armStockings) armStockings(k, o.armStockings === 'fishnet' ? fishnet(o.stocking ?? 0x15141a, 6, 8) : k.mat('dark', 0, .35, o.stocking ?? 0x15141a), trim);
    if (o.wings) wingPauldrons(k, trim);
    if (o.chain && !o.plain) bellyChain(k, trim, gem);
    if (o.pendant && !o.plain) pendant(k, trim, gem, s - .03);
  };
}

/** A sorceress: floor length with an oblique hem, slit high on the right side, a V to the waist, bell sleeves. */
costumeLooks['sorceress'] = gownLook({ hem: .035, flare: .075, slits: [1.3], slitTop: .96, slitWidth: .9, tilt: { amount: .26, phase: 1.3 }, plunge: .015, inner: .078, sleeves: 'bell', piping: true, pendant: true });
/** A dark queen: floor length, slit on both sides and at the back, bell sleeves, a tall collar and wings. */
costumeLooks['dark-queen'] = gownLook({ hem: .03, flare: .09, slits: [.5, -.5, Math.PI], slitTop: .98, slitWidth: .7, plunge: .02, inner: .075, sleeves: 'bell', collar: true, wings: true, stocking: 0x2a1230, piping: true, pendant: true });
/** A siren: a short sheath of plain cloth just above the stockings, sleeveless, with no metal on it. */
costumeLooks['siren'] = gownLook({ hem: 0, hemFromHip: -.01, flare: .04, slits: [], slitTop: 0, slitWidth: 0, plunge: .03, inner: .065, sleeves: 'none', stockings: true, stockingTop: -.12, strapless: true, plain: true, soft: { flare: .045, folds: 7, drop: .05 } });
/** A starlet: a backless halter minidress, close to the leg, with an embroidered hem and a ring of stones, an oblique cut and a slit high on the right, in opera gloves. */
costumeLooks['starlet'] = gownLook({ hem: 0, hemFromHip: -.34, flare: .015, slits: [1.2], slitTop: 1.0, slitWidth: .8, tilt: { amount: .12, phase: 1.2 }, plunge: .0, inner: .09, sleeves: 'gloves', backless: true, ornate: true, piping: true, pendant: true });
/** A warrior princess: a laced leather corset open in a V, a high-cut skirt of two flaps, thigh-high boots and bracers with gold cuffs. */
costumeLooks['warrior-princess'] = k => {
  const { h, crotch, waist, s, shoulder, wrist, elbow, neckBase, ankle, gold, ivory, dark } = parts(k);
  const hide = k.mat('leather', .15, .38);
  k.torsoLayer(crotch + .001, h + .06, .008, hide);
  const flaps = { top: h + .06, hem: h - .12, width: .118, dip: .045, flare: .02, ragged: 0 }, flap = k.loincloth(flaps);
  flap(flaps.hem, flaps.top, .018, hide); flap(flaps.hem - .004, flaps.hem + .012, .028, gold);
  k.torsoLayer(h + .02, h + .078, .03, hide); k.plate(gold, .03, .01, k.onTorso(0, h + .05, .036));
  vBodice(k, { top: s - .055, bottom: waist - .03, grow: .014, inner: .062, plunge: waist + .02, material: hide, shoulders: true });
  lacing(k, { top: s - .055, plunge: waist + .02, inner: .062, count: 5, material: ivory });
  k.torsoLayer(s - .06, s - .05, .026, gold, true);
  for (const side of [-1, 1]) {
    k.ribbon(k.run([side * .07, s - .055], [side * .035, neckBase - .012], .02, 1, 0, 6), .014, .006, hide);
    k.armLayer(wrist + .01, elbow + .03, .011, hide); k.armLayer(wrist + .01, wrist + .03, .02, gold);
    k.armPiece(side, shoulder - .13, shoulder - .113, .028, gold);
  }
  k.legLayer(ankle, h - .13, .011, hide); k.legLayer(h - .15, h - .125, .02, gold); k.legLayer(ankle + .1, ankle + .112, .02, dark);
};

/** A rakish hero: an open silk shirt and medallion, tight trousers into tall boots, a broad belt with a big buckle and a satin coat worn off the shoulder in the sleeves only. */
costumeLooks['rakish-hero'] = k => {
  const { h, crotch, waist, s, shoulder, armTop, wrist, knee, neckBase, ankle, gold, leather, clothDark } = parts(k);
  const coat = satin(k), shirt = k.mat('ivory', 0, .5); shirt.side = THREE.DoubleSide;
  k.torsoLayer(crotch + .001, h + .1, .01, clothDark); k.legLayer(ankle + .06, h + .05, .008, clothDark);
  k.legLayer(ankle, knee + .1, .012, leather); k.legLayer(knee + .08, knee + .115, .022, leather);
  vBodice(k, { top: s - .01, bottom: waist - .04, grow: .012, inner: .1, plunge: h + .06, material: shirt });
  k.torsoLayer(h + .015, h + .085, .034, leather); k.plate(gold, .045, .012, k.onTorso(0, h + .05, .04));
  const chain = Array.from({ length: 13 }, (_, i) => { const t = i / 12; return k.onTorso(lerp(-.08, .08, t), s - .01 - .1 * Math.sin(Math.PI * t), .03); });
  k.ribbon(chain, .008, .005, gold); k.plate(gold, .022, .008, chain[6]);
  for (const side of [-1, 1]) {
    k.armLayer(wrist + .005, armTop, .011, coat); k.armLayer(wrist + .005, wrist + .06, .022, shirt);
    k.armPiece(side, shoulder - .1, armTop, .021, coat); k.armPiece(side, shoulder - .104, shoulder - .094, .033, gold);
  }
  k.neckLoft(neckBase - .004, neckBase + .03, .016, coat);
};

/**
 * A harem dancer: a bandeau and a gold hip girdle with coins, and trousers that are fitted at the hip, billow from thigh to calf and are
 * gathered at the ankle, in sheer cloth, with gold bands and a groin cloth hanging from the girdle.
 */
costumeLooks['harem-dancer'] = k => {
  const { h, crotch, s, shoulder, elbow, wrist, ankle, gold, gem, clothDark } = parts(k);
  const sheer = satin(k), band = satin(k);
  k.torsoLayer(crotch + .001, h + .05, .008, clothDark);
  const low = ankle + .05, high = h + .03;
  // One loft with a bell profile: nothing at the hip or the ankle and fullest a third of the way up, so the cloth tapers into the body.
  const billow = (point: THREE.Vector3, facing: number, across: number) => {
    const t = clamp((point.y - low) / (high - low), 0, 1), extra = .085 * Math.sin(Math.PI * t ** .65) ** 1.2;
    point.x += across * extra; point.z += facing * extra;
  };
  for (const side of [-1, 1]) {
    k.legPiece(side, low, high, .016, sheer, billow);
    k.legPiece(side, ankle + .035, ankle + .06, .034, gold); k.legPiece(side, ankle - .01, ankle + .035, .02, gold);
  }
  vBodice(k, { top: s - .03, bottom: s - .15, grow: .014, inner: .03, plunge: s - .15, material: band, shoulders: true });
  k.torsoLayer(s - .035, s - .025, .024, gold, true);
  for (const side of [-1, 1]) k.ribbon(k.run([side * .03, s - .03], [side * .03, k.at.neckBase - .01], .02, 1, 0, 4), .014, .005, band);
  k.torsoLayer(h - .0, h + .09, .03, gold); k.stud(gem, k.onTorso(0, h + .045, .036), .018, .012);
  for (let i = 0; i < 9; i++) { const spot = k.onTorso(lerp(-.13, .13, i / 8), h - .005, .04).point; k.ellipsoid(k.hips, gold, spot.x, -.012, spot.z, .014, .014, .004); }
  for (const side of [-1, 1]) { k.armPiece(side, wrist, elbow + .02, .012, gold); k.armPiece(side, shoulder - .13, shoulder - .113, .028, gold); }
  groinCloth(k, { top: h + .09, length: .3, width: .066, point: .5, dip: .03, out: .03, grow: .042, material: band, trim: gold, fringe: gold, tassels: 7, stone: gem });
};

/**
 * A dragon lady: a cropped mandarin jacket with a tall collar and bell sleeves over a bare midriff, tight shorts and tall boots, with a
 * long silk groin cloth.
 */
costumeLooks['dragon-lady'] = k => {
  const { h, crotch, waist, s, shoulder, armTop, wrist, knee, neckBase, ankle, gold, gem, clothDark, leather } = parts(k);
  const silk = satin(k);
  k.torsoLayer(crotch + .001, h + .09, .01, silk); k.legLayer(h - .08, h + .05, .009, silk);
  vBodice(k, { top: s - .035, bottom: waist + .035, grow: .014, inner: .045, plunge: waist + .05, material: silk, shoulders: true });
  k.neckLoft(neckBase - .035, neckBase + .06, .02, silk); k.neckLoft(neckBase + .048, neckBase + .062, .028, gold);
  for (const side of [-1, 1]) {
    k.ribbon(k.run([side * .045, s - .035], [side * .036, neckBase - .015], .022, 1, 0, 5), .016, .006, silk);
    for (let i = 0; i < 3; i++) k.stud(gold, k.onTorso(side * (.05 + i * .004), waist + .09 + i * .045, .03), .009, .009); // frog buttons down the front edge
    k.armPiece(side, wrist, armTop - .02, .012, silk);
    for (let i = 0; i < 3; i++) k.armPiece(side, wrist - .004 + i * .045, wrist + .05 + i * .045, .05 - i * .012, silk);
    k.armPiece(side, wrist - .004, wrist + .008, .06, gold);
  }
  k.torsoLayer(waist - .035, waist + .02, .026, gold); k.stud(gem, k.onTorso(0, waist - .008, .034), .018, .012);
  k.legLayer(ankle, h - .06, .012, leather); k.legLayer(h - .085, h - .06, .02, clothDark);
  groinCloth(k, { top: waist - .04, length: .36, width: .058, point: .35, dip: .03, out: .03, grow: .042, material: silk, trim: gold, stone: gem });
  void shoulder; void knee;
};

/** A ballgown: a huge bell of a skirt with two separate flounces, a sweetheart bodice with a V cut down the back, puffed sleeves off the shoulder and a gold sash. */
costumeLooks['ballgown'] = k => {
  const { crotch, h, waist, s, shoulder, elbow, wrist, neckBase, ankle, gold, gem, clothDark } = parts(k);
  const silk = satin(k), hem = ankle + .02;
  k.torsoLayer(crotch + .001, h + .06, .008, clothDark);
  const skirt = { hem, flare: .36, grow: .016, top: waist + .02, bottom: hem, slitTop: 0, slits: [] as number[], width: 0, material: silk };
  splitGown(k, skirt);
  splitGown(k, { ...skirt, grow: .04, top: hem + .44, bottom: hem + .24 });
  splitGown(k, { ...skirt, grow: .056, top: hem + .19, bottom: hem + .02 });
  splitGown(k, { ...skirt, grow: .068, top: hem + .03, bottom: hem, material: gold });
  vBodice(k, { top: s - .035, bottom: waist - .04, grow: .013, inner: 0, plunge: waist, backInner: .075, backPlunge: waist - .02, material: silk });
  k.torsoLayer(waist - .045, waist + .03, .026, gold); k.stud(gem, k.onTorso(0, waist - .008, .032), .02, .014);
  for (const side of [-1, 1]) { k.armPiece(side, elbow - .02, shoulder - .02, .034, silk); k.armPiece(side, shoulder - .03, shoulder - .015, .046, gold); k.armLayer(wrist, elbow - .01, .009, silk); }
  k.neckLoft(neckBase - .004, neckBase + .03, .014, gold);
  k.ribbon(k.run([-.125, s - .04], [.125, s - .04], .032, 1, .01, 12), .012, .006, gold); // a trim along the neckline
  k.plate(gold, .03, .01, k.onTorso(0, s - .08, .04)); k.stud(gem, k.onTorso(0, s - .08, .05), .018, .012);
};

/** A fortune teller: an off-the-shoulder blouse over a skirt with two ruffled flounces, a coin belt and a gold hem. */
costumeLooks['fortune-teller'] = k => {
  const { crotch, h, waist, s, shoulder, elbow, wrist, neckBase, ankle, gold, gem, ivory, clothDark, silk } = parts(k);
  const skirt = satin(k), top = k.mat('ivory', 0, .75), hem = ankle + .08;
  k.torsoLayer(crotch + .001, h + .06, .008, clothDark);
  // One skirt, and two flounces standing clear of it and of each other, so no two sheets lie closer than a centimetre.
  k.gown(hem, .12, .016, skirt, hem, waist + .02);
  k.gown(hem, .12, .04, clothDark, hem + .3, hem + .48); k.gown(hem, .12, .062, silk, hem + .06, hem + .24);
  k.gown(hem, .12, .076, gold, hem, hem + .028);
  k.torsoLayer(waist - .03, s - .045, .013, top, true);
  for (const side of [-1, 1]) { k.armPiece(side, elbow - .04, shoulder - .03, .028, top); k.armPiece(side, wrist + .02, elbow, .01, top); k.armPiece(side, elbow - .045, elbow - .03, .04, gold); k.armPiece(side, shoulder - .04, shoulder - .028, .038, ivory); }
  k.torsoLayer(waist - .04, waist + .02, .03, clothDark);
  for (let i = 0; i < 10; i++) { const spot = k.onTorso(lerp(-.13, .13, i / 9), waist - .045, .04).point, [parent, origin] = k.anchor(waist); k.ellipsoid(parent, gold, spot.x, spot.y - origin, spot.z, .014, .014, .004); }
  k.neckLoft(neckBase, neckBase + .02, .012, gold);
  pendant(k, gold, gem, s - .02);
};

/** A shadow agent: a glossy leather bodysuit open in a V to the navel, crossed belts and holsters, long gloves and tall boots. */
costumeLooks['shadow-agent'] = k => {
  const { crotch, h, waist, s, wrist, armTop, knee, neckBase, ankle, gold, steel, dark } = parts(k);
  const suit = k.mat('cloth', .3, .28), strap = k.mat('leather', .1, .5, 0x2b1f1a);
  k.torsoLayer(crotch + .001, waist - .02, .008, suit); k.legLayer(ankle + .05, h + .05, .008, suit);
  vBodice(k, { top: neckBase - .02, bottom: waist - .04, grow: .011, inner: .06, plunge: waist - .02, material: suit, shoulders: true });
  k.armLayer(wrist, armTop, .008, suit);
  k.legLayer(ankle, knee + .06, .014, strap); k.legLayer(knee + .04, knee + .075, .024, gold);
  k.neckLoft(neckBase - .03, neckBase + .05, .016, suit);
  for (const side of [-1, 1]) {
    k.torsoLayer(h + .0, h + .03, .026, strap);
    k.ribbon(k.run([side * .1, h + .09], [-side * .1, h - .02], .034, 1, 0, 10), .028, .008, strap);
    k.box(k.hips, strap, .045, .17, .07, side * (k.at.pw + .045), -.12, 0); k.box(k.hips, steel, .02, .02, .05, side * (k.at.pw + .045), -.04, 0);
    k.armLayer(wrist + .003, wrist + .04, .018, dark);
  }
  k.torsoLayer(waist - .03, waist + .025, .03, strap); k.plate(gold, .03, .01, k.onTorso(0, waist - .003, .036));
};

/** A jester: a short tunic with a jagged hem and a ruff, one sleeve and one leg in each colour, and a bell at every point. */
costumeLooks['jester'] = k => {
  const { crotch, h, waist, s, armTop, wrist, neckBase, ankle, gold, silk, clothDark, cloth } = parts(k);
  const motley = satin(k), low = h - .22;
  k.torsoLayer(crotch + .001, h + .06, .008, clothDark);
  k.legPiece(-1, ankle + .06, h + .05, .008, cloth); k.legPiece(1, ankle + .06, h + .05, .008, silk);
  k.legLayer(ankle, ankle + .13, .012, k.m.leather); for (const side of [-1, 1]) k.legPiece(side, ankle + .125, ankle + .14, .02, gold);
  const zig: (low: number) => (point: THREE.Vector3, facing: number, across: number) => void = bottom => (point, facing, across) => {
    const w = clamp(1 - (point.y - bottom) / .1, 0, 1) ** 1.3, t = ((Math.atan2(facing, across) / (Math.PI * 2) * 6) % 1 + 1) % 1;
    point.y -= .09 * w * Math.abs(t * 2 - 1);
  };
  k.torsoLayer(waist - .03, s - .03, .012, cloth, true); k.torsoLayer(waist - .03, waist + .05, .022, silk);
  k.gown(low, .06, .018, motley, low, waist, zig(low)); k.gown(low, .06, .03, silk, low, low + .1, zig(low));
  k.armLayer(wrist, armTop, .01, cloth); k.armPiece(1, wrist, armTop, .02, silk);
  for (const side of [-1, 1]) k.armPiece(side, wrist - .004, wrist + .012, .028, gold);
  k.stack(neckBase - .02, neckBase + .03, .04, (a, b, g, m) => k.neckLoft(a, b, g, m), k.m.ivory, .018, .008);
  k.stud(gold, k.onTorso(0, waist + .02, .04), .016, .012);
};

/**
 * A vamp: a satin bodysuit cut in a deep V, a soft wrap skirt, a full ring cut on a diagonal (drawn up to the hip at the front right,
 * long at the back left) with a gold clasp at the hip, a tall collar, and sheer stockings on the arms and legs.
 */
costumeLooks['vamp'] = k => {
  const { crotch, h, waist, s, neckBase, gold, gem } = parts(k);
  const body = satin(k), wrap = satin(k), hem = h - .4;
  k.torsoLayer(crotch + .001, h + .09, .01, body); k.torsoLayer(h + .08, waist - .02, .01, body);
  vBodice(k, { top: s - .01, bottom: waist - .02, grow: .01, inner: .085, plunge: waist - .02, material: body, shoulders: true, armhole: true });
  // The wrap: a full ring of soft cloth, long at the back left and drawn up to the hip at the front right where the gold clasp holds it.
  softSkirt(k, { top: waist + .02, hem, grow: .026, flare: .05, folds: 5, drop: .03, tilt: { amount: .3, phase: .9 }, material: wrap });
  k.torsoLayer(waist - .04, waist + .03, .034, gold); k.stud(gem, k.onTorso(0, waist - .005, .042), .016, .012);
  k.plate(gold, .04, .012, k.onTorso(.115, h + .06, .05)); k.stud(gem, k.onTorso(.115, h + .06, .062), .02, .012);
  k.neckLoft(neckBase - .004, neckBase + .03, .014, gold);
  flaredCollar(k, body, gold);
  garters(k, k.mat('dark', 0, .35, 0x1c1420), gold, gem, h - .13);
  armStockings(k, k.mat('dark', 0, .35, 0x1c1420), gold);
  pendant(k, gold, gem, s - .02);
};

/**
 * A showgirl: a leotard cut very high at the leg, the openings rising almost to a broad gold belt, open in a V down the front, with
 * fishnet on the arms and legs held by garter straps from the belt, and a bow-tie collar.
 */
costumeLooks['showgirl'] = k => {
  const { crotch, h, waist, s, wrist, armTop, neckBase, ankle, gold, gem, silk } = parts(k);
  const body = satin(k), net = fishnet(0x17151c, 7, 16), sleeve = fishnet(0x17151c, 6, 8), belt = h + .1;
  highCut(k, { top: waist - .02, cut: belt - .005, bottom: crotch + .001, grow: .01, narrow: .04, material: body });
  vBodice(k, { top: s - .01, bottom: waist - .02, grow: .01, inner: .07, plunge: waist - .03, material: body, shoulders: true, armhole: true });
  k.torsoLayer(belt, waist - .01, .024, gold); k.stud(gem, k.onTorso(0, (belt + waist) / 2, .034), .016, .012);
  k.legLayer(ankle + .02, h + .0, .008, net); k.legLayer(h - .02, h + .0, .018, gold);
  for (const side of [-1, 1]) k.ribbon(k.run([side * .1, belt], [side * .12, h - .01], .03, 1, 0, 4), .012, .005, gold); // garter straps
  k.armLayer(wrist + .003, armTop - .02, .008, sleeve); k.armLayer(wrist + .003, wrist + .02, .018, gold);
  k.neckLoft(neckBase - .004, neckBase + .025, .016, k.m.ivory);
  for (const side of [-1, 1]) { const bow = k.ellipsoid(k.torso, silk, side * .035, neckBase - .02 - k.at.torsoY, k.onTorso(0, neckBase - .02, .03).point.z, .03, .02, .012); bow.rotation.z = side * .3; }
  k.stud(gem, k.onTorso(0, neckBase - .02, .034), .014, .012);
};

/**
 * The Berserker Furs: a belt of wound raw cloth, a long raw groin cloth of the same cloth in front and behind, torn at the hem, fur-cuffed boots with
 * bone bands, bracers, a pelt over one shoulder and a necklace of fangs. A woman wears a fur bandeau on leather halter straps; a man goes bare-chested
 * under a hide baldric. The Berserker's starting dress (`berserkerFurs` in `content/equipment/armor.ts`).
 */
export const berserkerFurs = (k: ArmorKit) => {
  const { h, crotch, waist, s, shoulder, armTop, wrist, knee, neckBase, ankle, ivory, leather, fur, dark } = parts(k);
  const hide = k.mat('leather', .05, .62), bone = k.mat('ivory', 0, .7);
  k.torsoLayer(crotch + .001, h + .06, .008, hide);
  if (k.female) {
    vBodice(k, { top: s - .035, bottom: s - .15, grow: .016, inner: .02, plunge: s - .15, material: fur, shoulders: true });
    for (const side of [-1, 1]) {
      k.ribbon(k.run([side * .03, s - .035], [side * .03, neckBase - .01], .026, 1, 0, 4), .014, .006, hide);
      k.stud(bone, k.onTorso(side * .06, s - .09, .036), .012, .014);
    }
  } else {
    // A broad hide baldric from the right shoulder to the left hip, front and back, with bone toggles down it.
    for (const face of [1, -1]) k.ribbon(k.run([.12, s + .005], [-.1, h + .125], .026, face, 0, 24), .05, .01, hide);
    for (const t of [.3, .55, .8]) k.stud(bone, k.onTorso(lerp(.12, -.1, t), lerp(s + .005, h + .125, t), .036), .012, .014);
  }
  const linen = k.mat('cloth', 0, .98, 0xb4a581); linen.side = THREE.DoubleSide;
  clothBelt(k, { y: h + .09, material: linen });
  groinCloth(k, { top: h + .06, length: .5, width: .07, point: .12, dip: .06, out: .02, grow: .03, material: linen, raw: .05, fringe: linen, tassels: 9 });
  k.legLayer(ankle, knee + .02, .013, hide); k.stack(knee - .06, knee + .08, .026, k.legLayer, fur, .03, .008);
  for (const y of [ankle + .09, ankle + .18]) k.legLayer(y, y + .014, .019, bone);
  for (const side of [-1, 1]) { k.armLayer(wrist + .01, wrist + .17, .013, hide); for (const y of [wrist + .04, wrist + .12]) k.armLayer(y, y + .012, .019, bone); k.armPiece(side, shoulder - .12, shoulder - .1, .024, hide); }
  k.stack(shoulder - .1, armTop, .03, (a, b, g, m) => k.armPiece(-1, a, b, g, m), fur, .035, .008); // a pelt over the left shoulder
  const chain = Array.from({ length: 11 }, (_, i) => { const t = i / 10; return k.onTorso(lerp(-.06, .06, t), s - .02 - .075 * Math.sin(Math.PI * t), .034); });
  k.ribbon(chain, .006, .004, hide); for (const i of [2, 4, 5, 6, 8]) k.stud(ivory, chain[i], .011, .02);
  void waist; void leather; void dark;
};
addArmorLook('berserker-furs', berserkerFurs);

/**
 * A gladiator: a chest wrapped in a leather band and crossed by a harness with a gold roundel, a belt of wound raw cloth and a long raw groin cloth of the same cloth, greaves, laced sandals, a guard over one arm and a great pauldron on the other shoulder.
 */
costumeLooks['gladiator'] = k => {
  const { h, crotch, waist, s, shoulder, armTop, elbow, wrist, knee, neckBase, ankle, gold, steel, leather, cloth } = parts(k);
  const strap = k.mat('leather', .05, .55, 0x3a2418);
  k.torsoLayer(crotch + .001, h + .06, .008, strap);
  for (const face of [1, -1]) { k.ribbon(k.run([-.125, s - .005], [.1, h + .13], .034, face, 0, 24), .04, .008, strap); k.ribbon(k.run([.125, s - .005], [-.1, h + .13], .034, face, 0, 24), .04, .008, strap); }
  const band = k.mat('leather', .08, .5, 0x4a2f1d); // a broad band of leather round the chest, the harness crossing over it
  k.torsoLayer(k.at.bustY - .058, k.at.bustY + .05, .026, band); k.plate(steel, .022, .008, k.onTorso(.0, k.at.bustY - .005, .03));
  const centre = k.onTorso(0, (s + h + .13) / 2 + .01, .05); k.plate(gold, .05, .012, centre); k.stud(steel, centre, .026, .016);
  const linen = k.mat('cloth', 0, .98, 0xc2b896); linen.side = THREE.DoubleSide;
  clothBelt(k, { y: h + .09, material: linen, side: -1 });
  groinCloth(k, { top: h + .06, length: .46, width: .07, point: .15, dip: .055, out: .02, grow: .03, material: linen, raw: .045, fringe: linen, tassels: 8 });
  k.legLayer(ankle, ankle + .1, .012, strap); for (const y of [ankle + .02, ankle + .05, ankle + .08]) k.legLayer(y, y + .01, .017, leather); // sandal laces
  k.legLayer(ankle + .12, knee + .0, .017, steel); k.legLayer(knee - .02, knee + .005, .022, gold);
  for (const limb of [k.leftLeg, k.rightLeg]) k.ellipsoid(limb.lower, steel, 0, .0, .066, .036, .038, .018);
  k.armLayer(wrist + .005, elbow + .1, .02, leather); for (const y of [wrist + .04, wrist + .1, wrist + .16]) k.armLayer(y, y + .012, .026, gold); // the guard over the right arm
  for (let i = 0; i < 3; i++) { const from = shoulder - .12 + i * .045; k.armPiece(-1, from, i === 2 ? armTop : from + .055, .03 + i * .007, steel); k.armPiece(-1, from - .003, from + .006, .036 + i * .007, gold); }
  k.neckLoft(neckBase - .004, neckBase + .02, .012, strap); void waist;
};

/**
 * A moon witch: a midnight corset with a silver crescent on the breastbone and a slit up the back laced across in silver, a tall collar, a belt of
 * moon phases, a short soft skirt cut on a diagonal under a ring of long tattered strips, fishnet on arms and legs held by silver bands and
 * ankle boots. Silver where the other costumes have gold.
 */
costumeLooks['moon-witch'] = k => {
  const { crotch, h, waist, s, wrist, neckBase, ankle, gem, dark, leather } = parts(k);
  const silver = k.mat('alloy', .8, .26), night = satin(k), rag = satin(k, 0x4a3470), net = fishnet(0x201428, 7, 16), sleeve = fishnet(0x201428, 6, 8);
  k.torsoLayer(crotch + .001, h + .06, .008, night);
  const top = s - .02, plunge = waist - .05, backInner = .05;
  vBodice(k, { top, bottom: waist - .05, grow: .013, inner: 0, plunge: waist, backInner, backPlunge: plunge, material: night, shoulders: true, armhole: true });
  for (let i = 0; i < 6; i++) { // lacing across the slit at the back
    const y = lerp(plunge + .02, top - .02, i / 5), half = backInner * clamp((y - plunge) / (top - plunge), 0, 1) + .006;
    k.ribbon(k.run([-half, y + .01], [half, y - .01], .02, -1, 0, 4), .009, .004, silver);
  }
  k.ribbon(k.run([-.12, top], [.12, top], .03, 1, .012, 12), .012, .006, silver); // silver along the neckline
  crescent(k, silver, 0, s - .095, .105);
  flaredCollar(k, night, silver, .3);
  k.torsoLayer(h + .03, h + .095, .03, leather); // the belt of phases: a full moon at the front and the moon waning to either side
  const phases = [.007, .01, .014, .02, .014, .01, .007];
  phases.forEach((size, i) => { const spot = k.onTorso(lerp(-.115, .115, i / 6), h + .062, .042).point, [parent, origin] = k.anchor(h + .062); k.ellipsoid(parent, silver, spot.x, spot.y - origin, spot.z, size, size, .005); });
  softSkirt(k, { top: waist - .02, hem: h - .26, grow: .024, flare: .04, folds: 6, drop: .04, tilt: { amount: .18, phase: -1.2 }, material: night });
  strips(k, { count: 9, top: h + .05, length: .52, flare: .15, width: .085, grow: .05, material: rag, tip: night });
  k.armLayer(wrist + .003, k.at.armTop - .02, .008, sleeve); k.armLayer(k.at.armTop - .035, k.at.armTop - .02, .016, silver);
  k.armLayer(wrist + .01, wrist + .09, .018, silver); k.armLayer(wrist + .085, wrist + .095, .022, night);
  garters(k, net, silver, gem, h - .1);
  k.legLayer(ankle, ankle + .13, .014, dark); k.legLayer(ankle + .12, ankle + .135, .02, silver);
  void neckBase;
};
